import { InfoIconDark } from "@/assets/images/svgIcons/infoIcon"
import { errorToastClassName, successToastClassName, appToast as toast } from "@/components/ui/baseUi/sonner"
import { Text } from "@/components/ui/baseUi/text"
import { useAppForm } from "@/components/ui/forms/hooks/useFormHook"
import { useConfirmPopup } from "@/modules/ui/components/popup/huConfirmPopup"
import { useCurrentBox } from "@modules/box/hooks/useBox"
import { BoxOfferFormFirstPage } from "@modules/boxOffer/components/boxOfferFormFirstPage"
import { BoxOfferFormSecondPage } from "@modules/boxOffer/components/boxOfferFormSecondPage"
import { BoxOfferFormThirdPage } from "@modules/boxOffer/components/boxOfferFormThirdPage"
import { BoxOfferResumeView } from "@modules/boxOffer/components/boxOfferResumeView"
import { BoxOfferDetailedEntity, BoxOfferDraftEntity } from "@modules/boxOffer/entities/boxOfferEntity"
import { BoxOfferType } from "@modules/boxOffer/entities/boxOfferTypes"
import { useBoxOffer } from "@modules/boxOffer/hooks/useBoxOffer"
import { type BoxOfferFormValues, getBoxOfferValidationSchema } from "@modules/boxOffer/types/boxOfferFormTypes"
import { word } from "@modules/core/utils/i18n"
import { DEFAULT_MAXIMUM_AMOUNT } from "@modules/currency/currency"
import { useLanguage } from "@modules/language/hooks/useLanguage"
import { useSidebar } from "@modules/sidebar/hooks/useSidebar"
import { LoggerType } from "@modules/utils/loggerUtils"
import { useEffect, useMemo, useRef, useState } from "react"

export type BoxOfferFormPageProps = {
  setPageNumber: React.Dispatch<React.SetStateAction<number>>
  handleSubmit: () => void
  isArchiveEdition?: boolean
  isEdition?: boolean
}

type BoxOfferFormProps = {
  offer?: BoxOfferDetailedEntity
  selectedType?: BoxOfferType
  isArchiveEdition?: boolean
}

export function BoxOfferForm({ offer, selectedType, isArchiveEdition }: BoxOfferFormProps) {
  const { result: currentBox } = useCurrentBox()
  const [pageNumber, setPageNumber] = useState(1)
  const language = useLanguage()
  const containerRef = useRef<HTMLDivElement>(null)
  const { createBoxOffer, updateOffer: updateOfferMutation, isPending } = useBoxOffer(offer?.id)
  const { hideSidebar } = useSidebar()
  const confirmPopup = useConfirmPopup()

  const boxOfferDraft: BoxOfferFormValues = useMemo(
    () => BoxOfferDraftEntity.toDraft(offer, selectedType, currentBox?.stripeAccountCurrency),
    [offer, selectedType, currentBox?.stripeAccountCurrency],
  )

  const validationSchema = getBoxOfferValidationSchema(
    language,
    pageNumber,
    currentBox?.stripeMinimumAmount ?? 0,
    currentBox?.stripeMaximumAmount ?? DEFAULT_MAXIMUM_AMOUNT,
    currentBox?.stripeAccountCurrency,
  )

  const form = useAppForm({
    defaultValues: boxOfferDraft,
    validators: {
      onSubmit: validationSchema,
      onChange: validationSchema,
    },
    // Without this, handleSubmit bails out while stale errors exist instead of re-validating
    canSubmitWhenInvalid: true,
    onSubmitInvalid: () => {
      requestAnimationFrame(() => {
        const errorElements = containerRef.current?.querySelectorAll('[aria-invalid="true"]')
        if (errorElements && errorElements.length > 0) {
          document
            .querySelector('[data-slot="right-sidebar-content"]')
            ?.scrollTo({ top: (errorElements[0] as HTMLElement).offsetTop - 30, behavior: "smooth" })
        }
      })
    },
    onSubmit: async ({ value }) => {
      if (pageNumber < 4) {
        setPageNumber((prevState) => prevState + 1)
        return
      }

      const updateOffer = async () => {
        await updateOfferMutation(BoxOfferDraftEntity.fromState(value))
        toast.success(word("offer.form.update.success"), {
          classNames: successToastClassName(),
        })

        hideSidebar(false)
      }

      try {
        if (offer) {
          const modaleMessage = (
            <div className="flex w-full flex-col gap-4">
              <div className="flex w-full flex-col items-center justify-center gap-2">
                <InfoIconDark color="" />
                <Text fontStyle="TL">{word(`offer.form.${value.type}.edit`)}</Text>
              </div>
              <Text fontStyle="BM" className="text-textDefault-caption" style={{ whiteSpace: "pre-line" }}>
                {word("box.offer.form.priceChangeWarning.content")}
              </Text>
            </div>
          )

          if (
            boxOfferDraft.price !== value.price ||
            boxOfferDraft.currency !== value.currency ||
            boxOfferDraft.minimumMonthlyDuration !== value.minimumMonthlyDuration ||
            boxOfferDraft.commitmentPeriodAutoRenewal !== value.commitmentPeriodAutoRenewal ||
            boxOfferDraft.recurringPeriod !== value.recurringPeriod
          ) {
            confirmPopup.show({
              title: "",
              message: modaleMessage,
              accept: async () => {
                await updateOffer()
              },
              footerProps: {
                confirmText: word("global.check"),
                cancelText: word("global.cancel"),
              },
            })
          } else {
            await updateOffer()
          }
        } else {
          await createBoxOffer(BoxOfferDraftEntity.fromState(value))
          toast.success(word("offer.form.create.success"), {
            classNames: successToastClassName(),
          })
          hideSidebar(false)
        }
      } catch (e) {
        console.error(LoggerType.Box + "create/update offer", e)
        toast.error(word("global.error.label"), {
          classNames: errorToastClassName(),
        })
      }
    },
  })

  useEffect(() => {
    if (pageNumber) {
      document.querySelector('[data-slot="right-sidebar-content"]')?.scrollTo({ top: 0 })
    }
  }, [pageNumber])

  const pageProps: BoxOfferFormPageProps = {
    isArchiveEdition,
    isEdition: !!offer,
    handleSubmit: () => void form.handleSubmit(),
    setPageNumber,
  }

  return (
    <div ref={containerRef}>
      {pageNumber === 1 && <BoxOfferFormFirstPage form={form} {...pageProps} />}
      {pageNumber === 2 && <BoxOfferFormSecondPage form={form} {...pageProps} />}
      {pageNumber === 3 && <BoxOfferFormThirdPage form={form} {...pageProps} />}
      {pageNumber === 4 && (
        <BoxOfferResumeView
          formOffer={BoxOfferDraftEntity.fromState(form.state.values)}
          leftButtonAction={() => {
            setPageNumber((prevState) => prevState - 1)
          }}
          rightButtonAction={() => void form.handleSubmit()}
          loading={isPending}
        />
      )}
    </div>
  )
}
